import {
	IconAlertCircle,
	IconChecks,
	IconClock,
	IconDeviceSim,
	IconPlus,
	IconTrophy,
	IconCash,
	IconTrendingUp,
	IconCalendar,
} from "@tabler/icons-react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { AppShell } from "@/components/layout/app-shell"
import { ProtectedRoute } from "@/components/layout/protected-route"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card"
import { useAgentSIMs } from "@/hooks/use-agent-sims"
import { useAgentStats } from "@/hooks/use-agent-stats"

export const Route = createFileRoute("/")({
	component: Dashboard,
})

function Dashboard() {
	return (
		<ProtectedRoute>
			<AppShell>
				<DashboardContent />
			</AppShell>
		</ProtectedRoute>
	)
}

function DashboardContent() {
	const { data: sims, isLoading: simsLoading, error: simsError } = useAgentSIMs()
	const { data: stats, isLoading: statsLoading, error: statsError } = useAgentStats()

	// Handle loading state
	if (simsLoading || statsLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-center">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
					<p className="text-muted-foreground">Loading dashboard...</p>
				</div>
			</div>
		)
	}

	// Handle error state
	if (simsError || statsError) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-center">
					<IconAlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
					<p className="text-destructive font-medium">Failed to load dashboard</p>
					<p className="text-sm text-muted-foreground mt-2">
						{(simsError || statsError) instanceof Error
							? (simsError || statsError)?.message
							: "Unknown error occurred"}
					</p>
				</div>
			</div>
		)
	}

	// Calculate SIM statistics
	const simStats = {
		totalSIMs: sims?.length || 0,
		activeSIMs: sims?.filter((sim) => sim.status === "active").length || 0,
		pendingSIMs: sims?.filter((sim) => sim.status === "pending").length || 0,
		rejectedSIMs: sims?.filter((sim) => sim.status === "rejected").length || 0,
	}

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-3xl font-bold tracking-tight">Agent Dashboard</h1>
				<p className="text-muted-foreground">
					Welcome back, {stats?.agent?.name || "Agent"}. Manage your SIMs and view your performance.
				</p>
			</div>

			{/* Transaction Stats */}
			<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Today's Earnings</CardTitle>
						<IconCash className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-green-600">
							৳{stats?.earnings?.today_earnings?.toLocaleString() || "0"}
						</div>
						<p className="text-xs text-muted-foreground">
							{stats?.transactions?.today?.count || 0} transactions
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">This Month</CardTitle>
						<IconCalendar className="h-4 w-4 text-blue-500" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-blue-600">
							৳{stats?.earnings?.month_earnings?.toLocaleString() || "0"}
						</div>
						<p className="text-xs text-muted-foreground">
							{stats?.transactions?.this_month?.count || 0} transactions
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">All Time Volume</CardTitle>
						<IconTrendingUp className="h-4 w-4 text-purple-500" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-purple-600">
							৳{stats?.transactions?.all_time?.volume?.toLocaleString() || "0"}
						</div>
						<p className="text-xs text-muted-foreground">
							{stats?.transactions?.all_time?.count || 0} total transactions
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Commission Rate</CardTitle>
						<IconTrophy className="h-4 w-4 text-green-500" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-green-600">
							{(stats?.agent?.commission_rate || 0) * 100}%
						</div>
						<p className="text-xs text-muted-foreground">
							Your commission rate
						</p>
					</CardContent>
				</Card>
			</div>

			{/* SIM Stats */}
			<div className="grid gap-4 md:grid-cols-4">
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Total SIMs</CardTitle>
						<IconDeviceSim className="h-4 w-4 text-muted-foreground" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{stats?.sims?.total || simStats.totalSIMs}</div>
						<p className="text-xs text-muted-foreground">
							Registered SIM cards
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Active SIMs</CardTitle>
						<IconChecks className="h-4 w-4 text-green-500" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-green-600">
							{stats?.sims?.active || simStats.activeSIMs}
						</div>
						<p className="text-xs text-muted-foreground">
							Ready for transactions
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Pending</CardTitle>
						<IconClock className="h-4 w-4 text-orange-500" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-orange-600">
							{simStats.pendingSIMs}
						</div>
						<p className="text-xs text-muted-foreground">Awaiting approval</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Inactive</CardTitle>
						<IconAlertCircle className="h-4 w-4 text-gray-500" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-gray-600">
							{stats?.sims?.inactive || simStats.rejectedSIMs}
						</div>
						<p className="text-xs text-muted-foreground">Not active</p>
					</CardContent>
				</Card>
			</div>

			{/* Quick Actions */}
			<div className="grid gap-4 md:grid-cols-2">
				<Card className="border-dashed">
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<IconPlus className="h-5 w-5" />
							Register New SIM
						</CardTitle>
						<CardDescription>
							Add a new SIM card to your account for transactions
						</CardDescription>
					</CardHeader>
					<CardContent>
						<Button asChild className="w-full">
							<Link to="/sims/register">
								<IconPlus className="h-4 w-4 mr-2" />
								Register SIM
							</Link>
						</Button>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<IconDeviceSim className="h-5 w-5" />
							My SIMs
						</CardTitle>
						<CardDescription>
							View and manage all your registered SIM cards
						</CardDescription>
					</CardHeader>
					<CardContent>
						<Button asChild variant="outline" className="w-full">
							<Link to="/sims/my-sims">
								<IconDeviceSim className="h-4 w-4 mr-2" />
								View All SIMs
							</Link>
						</Button>
					</CardContent>
				</Card>
			</div>

			{/* Recent Orders */}
			{stats?.recent_orders && stats.recent_orders.length > 0 && (
				<Card>
					<CardHeader>
						<CardTitle>Recent Orders</CardTitle>
						<CardDescription>
							Your latest transaction activity
						</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="space-y-4">
							{stats.recent_orders.slice(0, 5).map((order) => (
								<div
									key={order.id}
									className="flex items-center justify-between p-4 border rounded-lg"
								>
									<div className="space-y-1">
										<div className="flex items-center gap-2">
											<span className="font-mono font-medium">
												৳{order.amount.toLocaleString()}
											</span>
											<Badge
												variant={
													order.status === "completed"
														? "default"
														: "secondary"
												}
											>
												{order.status}
											</Badge>
										</div>
										<p className="text-sm text-muted-foreground">
											{new Date(order.created_at).toLocaleString()}
										</p>
									</div>
								</div>
							))}
						</div>
					</CardContent>
				</Card>
			)}

			{/* Help Section */}
			<Card>
				<CardHeader>
					<CardTitle>Getting Started</CardTitle>
					<CardDescription>
						Quick guide to using your agent dashboard
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="grid gap-4 md:grid-cols-2">
						<div className="space-y-2">
							<h4 className="font-medium flex items-center gap-2">
								<span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm flex items-center justify-center font-medium">
									1
								</span>
								Register SIM Cards
							</h4>
							<p className="text-sm text-muted-foreground">
								Add your mobile financial service SIM cards to start processing
								transactions
							</p>
						</div>

						<div className="space-y-2">
							<h4 className="font-medium flex items-center gap-2">
								<span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm flex items-center justify-center font-medium">
									2
								</span>
								Wait for Approval
							</h4>
							<p className="text-sm text-muted-foreground">
								Admin will review and approve your SIM registrations
							</p>
						</div>

						<div className="space-y-2">
							<h4 className="font-medium flex items-center gap-2">
								<span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm flex items-center justify-center font-medium">
									3
								</span>
								Configure Devices
							</h4>
							<p className="text-sm text-muted-foreground">
								Use the Agent Device App to bind approved SIMs to your devices
							</p>
						</div>

						<div className="space-y-2">
							<h4 className="font-medium flex items-center gap-2">
								<span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-sm flex items-center justify-center font-medium">
									4
								</span>
								Start Earning
							</h4>
							<p className="text-sm text-muted-foreground">
								Process transactions and earn commissions on successful payments
							</p>
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	)
}
